import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Sub-section chip bar shown directly under the top Navbar for the three
 * aggregated pages (Content / Schedule / Money).
 *
 * Lives globally in Layout.jsx — renders nothing on routes outside those
 * sections so it's free to leave mounted at all times.
 *
 * Each chip is a NavLink, so the active chip highlights from the URL.
 * Clicking a chip navigates to the underlying existing page (e.g.
 * /content/files → /media/gallery) — we do NOT embed the inner page,
 * we link to its real route so bookmarks + back-button keep working.
 */

const SECTIONS = [
  {
    key:  'content',
    rootPaths: ['/content', '/media', '/playlist', '/links'],
    chips: [
      { i18n: 'nav.content_all',       to: '/content' },
      { i18n: 'nav.content_files',     to: '/media/gallery', alt: ['/media'] },
      { i18n: 'nav.content_playlists', to: '/playlist/list', alt: ['/playlist'] },
      { i18n: 'nav.content_links',     to: '/links' },
    ],
  },
  {
    key:  'schedule',
    rootPaths: ['/schedule', '/apps/match-scheduler', '/apps/birthday', '/apps/prayer', '/apps/happy-hour', '/apps/spin-win'],
    chips: [
      { i18n: 'nav.schedule_upcoming',   to: '/schedule' },
      { i18n: 'nav.schedule_matches',    to: '/apps/match-scheduler' },
      { i18n: 'nav.schedule_birthday',   to: '/apps/birthday' },
      { i18n: 'nav.schedule_happy_hour', to: '/apps/happy-hour' },
      { i18n: 'nav.schedule_spin_win',   to: '/apps/spin-win' },
      { i18n: 'nav.schedule_prayer',     to: '/apps/prayer' },
    ],
  },
  {
    key:  'money',
    rootPaths: ['/money', '/billing', '/apps/ad-marketplace', '/apps/smart-ad-scheduler', '/profile'],
    chips: [
      { i18n: 'nav.money_overview',    to: '/money' },
      { i18n: 'nav.money_plan',        to: '/profile', state: { activeTab: 'billing' } },
      { i18n: 'nav.money_earnings',    to: '/billing/ad-earnings' },
      { i18n: 'nav.money_ad_settings', to: '/billing/ads-settings' },
      { i18n: 'nav.money_marketplace', to: '/apps/ad-marketplace' },
    ],
  },
];

function pathStartsWithAny(pathname, prefixes) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function SectionChipBar({ only = null }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const active = SECTIONS.filter((s) => !only || only.includes(s.key)).find((s) => pathStartsWithAny(pathname, s.rootPaths));
  if (!active) return null;

  // Determine which chip should be highlighted. The longest matching
  // prefix wins so deeper paths (e.g. /media/gallery) don't lose to the
  // shorter "/content" "All" chip.
  const matchChip = (chip) => {
    if (pathname === chip.to) return chip.to.length;
    if (pathname.startsWith(chip.to + '/')) return chip.to.length;
    for (const alt of (chip.alt ?? [])) {
      if (pathname === alt || pathname.startsWith(alt + '/')) return alt.length;
    }
    return 0;
  };
  let bestScore = 0;
  let bestKey   = null;
  for (const c of active.chips) {
    const s = matchChip(c);
    if (s > bestScore) { bestScore = s; bestKey = c.i18n; }
  }

  return (
    <div className="pp-chip-bar">
      <div className="pp-chip-bar-inner">
        {active.chips.map((c) => {
          const isActive = c.i18n === bestKey;
          return (
            <NavLink
              key={c.i18n}
              to={c.to}
              state={c.state}
              className={`pp-chip ${isActive ? 'pp-chip-active' : ''}`}
            >
              {t(c.i18n)}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
